import { VespaRankingProfile } from '@pushly/models/lib/enums/recommendations/vespa/vespa-ranking-profile'
import { VespaRecommendationConfiguration } from '@pushly/models/lib/structs/journeys'
import { RelativeDate } from '@pushly/models/lib/structs/relative-date'
import { RelativeDateDisplayMetric } from '@pushly/models/lib/enums/relative-date-display-metric'

export { INDEX_DISPLAY_DEFAULT_ID } from '../constants'

/**
 * Customer-facing name for this recommendation source.
 *
 * Deliberately does NOT say "Vespa" — Vespa is our own infrastructure, not
 * something the customer has a relationship with (unlike BlueConic, which
 * they license and connect themselves). Branding the display layer keeps the
 * engine an implementation detail we can swap without a customer-visible
 * rename, and avoids a dropdown that reads "a CDP I pay for" next to "a
 * search engine I've never heard of".
 *
 * Only the display layer is branded — RecommendationSource.VESPA, the model
 * classes, and FEAT_INTEGRATIONS_VESPA_REC all intentionally keep the Vespa
 * name (that flag in particular is load-bearing across jet-stream,
 * coral-snowflake, and submarine-communications-cable).
 */
export const PUSHLY_AI_CURATOR_DISPLAY_VALUE = 'Pushly AI Curator'
export const MAX_VESPA_RECOMMENDATION_CONFIGS_ALLOWED = 6

export const VESPA_RANKING_PROFILE_DISPLAY_NAMES: Record<VespaRankingProfile, string> = {
    [VespaRankingProfile.HEURISTIC]: 'Heuristic',
    [VespaRankingProfile.HYBRID]: 'Hybrid',
}

/**
 * Default freshness_max_age for a brand-new configuration — 12 hours, matching
 * VespaContentWindow/secondsToContentWindow's own default so a new config's
 * displayed Content Window and its persisted value agree from the moment it's
 * created. A new config must not start with freshness_max_age: null: the
 * Content Window UI always shows a concrete window (there's no "unset" UI
 * state — see vespa-content-window.tsx), so a null default here would rely on
 * vespa-configuration-editor.tsx's mount-time self-heal effect to persist the
 * displayed default on first render, which is meant for true legacy configs
 * saved before Content Window existed, not for configs created today.
 */
const DEFAULT_CONTENT_WINDOW_SECONDS = 12 * 60 * 60

// New configs default to the Balanced preset (dial stop 2 on both dials —
// weight 1.0, parity with the always-on base RRF terms) under the
// ranking-weights model: ranking_profile is always HYBRID for dial-driven
// configs (see weightsToProfile in vespa-configuration-editor.tsx), and
// personalized_weight/recent_weight are set explicitly rather than left null
// so the config starts in a well-defined, named-preset state rather than
// falling back through legacyProfileToWeights to an ambiguous "Custom" state.
export const defaultVespaConfigurationValue = VespaRecommendationConfiguration.from({
    id: 'first',
    ranking_profile: VespaRankingProfile.HYBRID,
    count: 1,
    freshness_max_age: RelativeDate.from({
        intervalSeconds: DEFAULT_CONTENT_WINDOW_SECONDS,
        displayMetric: RelativeDateDisplayMetric.HOURS,
    }),
    freshness_half_response: null,
    filters: null,
    personalized_weight: 1.0,
    recent_weight: 1.0,
})
